import React from 'react';
import styled, { css } from 'styled-components';
import { colors, typography, spacing, borderRadius, shadows, animations } from '../styles/designTokens';

export type KeywordStatusVariant = 'active' | 'inactive';

export interface KeywordStatusSplitCardProps {
  variant: KeywordStatusVariant;
  autoCount: number;
  manualCount: number;
  formatNumber: (n: number) => string;
  clickable?: boolean;
  isActive?: boolean;
  onClick?: () => void;
}

const CardContainer = styled.div<{ $clickable: boolean; $isActive: boolean }>`
  background: ${colors.semantic.background.primary};
  border: 1px solid
    ${({ $isActive, $clickable }) => {
      if ($isActive) return colors.primary[300];
      if ($clickable) return colors.semantic.border.secondary;
      return colors.semantic.border.primary;
    }};
  border-radius: ${borderRadius.xl};
  padding: ${spacing[6]};
  box-shadow: ${({ $isActive, $clickable }) => {
    if ($isActive) return shadows.primary;
    if ($clickable) return shadows.md;
    return shadows.sm;
  }};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: all ${animations.duration.normal} ${animations.easing.smooth};
  transform: ${({ $isActive }) => ($isActive ? 'translateY(-4px)' : 'none')};
  position: relative;
  overflow: hidden;

  ${({ $clickable }) =>
    $clickable &&
    css`
      &::before {
        content: '';
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        height: 3px;
        background: linear-gradient(90deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%);
        opacity: 0.6;
      }
    `}

  ${({ $isActive }) =>
    $isActive &&
    css`
      background: linear-gradient(135deg, ${colors.primary[50]} 0%, ${colors.semantic.background.primary} 100%);
      border-color: ${colors.primary[400]};
      &::before {
        opacity: 1;
      }
    `}

  &:hover {
    ${({ $clickable }) =>
      $clickable &&
      css`
        transform: translateY(-4px);
        box-shadow: ${shadows.lg};
        border-color: ${colors.primary[400]};
        background: linear-gradient(135deg, ${colors.primary[50]} 0%, ${colors.semantic.background.primary} 100%);
        &::before {
          opacity: 1;
        }
      `}
  }
`;

const Title = styled.h3`
  margin: 0 0 ${spacing[4]} 0;
  font-size: ${typography.fontSize.sm};
  color: ${colors.semantic.text.tertiary};
  font-weight: ${typography.fontWeight.medium};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const MainValue = styled.div`
  font-size: ${typography.fontSize['3xl']};
  font-weight: ${typography.fontWeight.bold};
  color: ${colors.semantic.text.primary};
  line-height: 1.2;
  margin-bottom: ${spacing[3]};

  @media (max-width: 640px) {
    font-size: ${typography.fontSize['2xl']};
  }
`;

const SectionLabel = styled.div`
  font-size: 12px;
  font-weight: 800;
  color: ${colors.semantic.text.secondary};
  margin: 0 0 ${spacing[2]} 0;
`;

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px 10px;
`;

const MetaChip = styled.div<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  border: 1px solid ${colors.semantic.border.primary};
  background: ${colors.secondary[50]};
  color: ${({ $color }) => $color || colors.secondary[600]};
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
`;

const ChipLabel = styled.span`
  font-weight: 900;
`;

const ChipValue = styled.span`
  font-weight: 900;
`;

const titles: Record<KeywordStatusVariant, string> = {
  active: '키워드 현황 (활성)',
  inactive: '키워드 현황 (비활성)'
};

export default function KeywordStatusSplitCard({
  variant,
  autoCount,
  manualCount,
  formatNumber,
  clickable = false,
  isActive = false,
  onClick
}: KeywordStatusSplitCardProps) {
  const total = autoCount + manualCount;
  const isActiveTone = variant === 'active';

  return (
    <CardContainer
      $clickable={clickable}
      $isActive={isActive}
      onClick={clickable ? onClick : undefined}
      onKeyDown={
        clickable && onClick
          ? e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onClick();
              }
            }
          : undefined
      }
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      aria-pressed={clickable ? isActive : undefined}
    >
      <Title>{titles[variant]}</Title>

      <MainValue>
        {formatNumber(total)}
        <span
          style={{
            fontSize: '14px',
            marginLeft: '4px',
            fontWeight: 800,
            color: colors.semantic.text.tertiary
          }}
        >
          건
        </span>
      </MainValue>

      <SectionLabel>자동·수동</SectionLabel>
      <ChipRow>
        <MetaChip
          $color={isActiveTone ? colors.success[600] : colors.semantic.text.tertiary}
          title={`자동: ${autoCount}`}
        >
          <ChipLabel>자동</ChipLabel>
          <ChipValue>{formatNumber(autoCount)}</ChipValue>
        </MetaChip>
        <MetaChip
          $color={isActiveTone ? colors.error[600] : colors.semantic.text.tertiary}
          title={`수동: ${manualCount}`}
        >
          <ChipLabel>수동</ChipLabel>
          <ChipValue>{formatNumber(manualCount)}</ChipValue>
        </MetaChip>
      </ChipRow>
    </CardContainer>
  );
}
